import { useState } from 'react';

import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { Switch } from '@/design-system/ui/Switch';
import { COOKIE_CATEGORIES, type CookieChoice } from '@escala/domain/legal';
import { getConsent, saveConsent } from '@/privacy/consent';

import { CookieCategoryCard } from './LegalContent';

type Optional = { functional: boolean; analytics: boolean };

/**
 * "Preferências de cookies": cada categoria com o que guarda; os essenciais ficam sempre
 * ligados e as opcionais começam como estão (desligadas, se a pessoa ainda não escolheu —
 * nada vem marcado de antemão).
 */
export default function CookiePreferencesDialog({ onClose }: { onClose: () => void }) {
  const [choice, setChoice] = useState<Optional>(() => {
    const consent = getConsent();

    return { functional: consent?.functional ?? false, analytics: consent?.analytics ?? false };
  });

  const save = (value: CookieChoice) => {
    saveConsent(value);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(580px,calc(100vw-24px))]" described>
        <DialogHeader
          title="Preferências de cookies"
          description="Escolha o que podemos guardar no seu navegador. Você pode mudar quando quiser."
        />
        <DialogBody className="gap-2.5">
          {COOKIE_CATEGORIES.map((category) => {
            if (category.required) {
              return <CookieCategoryCard key={category.key} category={category} state="Sempre ativos" />;
            }

            const key = category.key as keyof Optional;

            return (
              <CookieCategoryCard
                key={category.key}
                category={category}
                state={choice[key] ? 'Ligados' : 'Desligados'}
                control={
                  <Switch
                    aria-label={`Cookies ${category.title.toLowerCase()}`}
                    checked={choice[key]}
                    onCheckedChange={(on) => setChoice((c) => ({ ...c, [key]: on }))}
                    className="mt-0.5"
                  />
                }
              />
            );
          })}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => save(choice)}>
            Salvar escolhas
          </Button>
          <Button variant="secondary" onClick={() => save('all')}>
            Aceitar todos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
